import type { Facing } from '../contracts/presentation-snapshot';
import type { AttackInstance } from '../combat/attack';
import { cloneAttackInstance } from '../combat/attack';
import type { CombatEffectRuntime } from '../combat/systems';
import { cloneCombatEffectRuntime } from '../combat/systems';
import { ActionInputBuffer } from '../input/action-input-buffer';
import type { CharacterState } from './character-state';
import { TransitionReason } from './transition';
import type { TransitionReason as TransitionReasonType } from './transition';

export interface MutableVector {
  x: number;
  y: number;
}

export interface ActiveEffectRuntime {
  id: string;
  remainingFrames: number;
  tags: Set<string>;
  parameters: Map<string, number>;
}

export type CurrentAttackRuntime = AttackInstance;

export interface CharacterRuntimeState {
  id: string;
  characterDefinitionId: string;
  maxHp: number;
  hp: number;
  gauge: number;
  position: MutableVector;
  velocity: MutableVector;
  pendingKnockback: MutableVector | null;
  isGrounded: boolean;
  facing: Facing;
  state: CharacterState;
  stateFrame: number;
  lastTransitionReason: TransitionReasonType;
  currentAttack: CurrentAttackRuntime | null;
  combatEffects: Map<string, CombatEffectRuntime>;
  stunFramesRemaining: number;
  stunAppliedLogicalFrame: number;
  knockdownFramesRemaining: number;
  wakeFramesRemaining: number;
  lifecycleAppliedLogicalFrame: number;
  postWakeInvincibilityFrames: number;
  postWakeAppliedLogicalFrame: number;
  inputBuffer: ActionInputBuffer;
  activeEffects: Map<string, ActiveEffectRuntime>;
  transientFlags: Set<string>;
  isAirborneInvincible: boolean;
  landingRecoveryFramesRemaining: number;
  landingRecoveryAppliedLogicalFrame: number;
}

export const createCharacterRuntimeState = (
  id: string,
  facing: Facing,
  position: Readonly<MutableVector> = { x: 0, y: 0 },
  characterDefinitionId = id,
): CharacterRuntimeState => {
  if (id.length === 0) throw new RangeError('Character id must not be empty.');
  return {
    id,
    characterDefinitionId,
    maxHp: 1_000,
    hp: 1_000,
    gauge: 0,
    position: { ...position },
    velocity: { x: 0, y: 0 },
    pendingKnockback: null,
    isGrounded: false,
    facing,
    state: 'Idle',
    stateFrame: 0,
    lastTransitionReason: TransitionReason.BATTLE_RESET,
    currentAttack: null,
    combatEffects: new Map(),
    stunFramesRemaining: 0,
    stunAppliedLogicalFrame: -1,
    knockdownFramesRemaining: 0,
    wakeFramesRemaining: 0,
    lifecycleAppliedLogicalFrame: -1,
    postWakeInvincibilityFrames: 0,
    postWakeAppliedLogicalFrame: -1,
    inputBuffer: new ActionInputBuffer(),
    activeEffects: new Map(),
    transientFlags: new Set(),
    isAirborneInvincible: false,
    landingRecoveryFramesRemaining: 0,
    landingRecoveryAppliedLogicalFrame: -1,
  };
};

export const cloneCharacterRuntimeState = (source: Readonly<CharacterRuntimeState>): CharacterRuntimeState => ({
  id: source.id,
  characterDefinitionId: source.characterDefinitionId,
  maxHp: source.maxHp,
  hp: source.hp,
  gauge: source.gauge,
  position: { ...source.position },
  velocity: { ...source.velocity },
  pendingKnockback: source.pendingKnockback === null ? null : { ...source.pendingKnockback },
  isGrounded: source.isGrounded,
  facing: source.facing,
  state: source.state,
  stateFrame: source.stateFrame,
  lastTransitionReason: source.lastTransitionReason,
  currentAttack: source.currentAttack === null ? null : cloneAttackInstance(source.currentAttack),
  combatEffects: new Map([...source.combatEffects].map(([key, effect]) => [key, cloneCombatEffectRuntime(effect)])),
  stunFramesRemaining: source.stunFramesRemaining,
  stunAppliedLogicalFrame: source.stunAppliedLogicalFrame,
  knockdownFramesRemaining: source.knockdownFramesRemaining,
  wakeFramesRemaining: source.wakeFramesRemaining,
  lifecycleAppliedLogicalFrame: source.lifecycleAppliedLogicalFrame,
  postWakeInvincibilityFrames: source.postWakeInvincibilityFrames,
  postWakeAppliedLogicalFrame: source.postWakeAppliedLogicalFrame,
  inputBuffer: source.inputBuffer.clone(),
  activeEffects: new Map([...source.activeEffects].map(([key, effect]) => [key, {
    id: effect.id,
    remainingFrames: effect.remainingFrames,
    tags: new Set(effect.tags),
    parameters: new Map(effect.parameters),
  }])),
  transientFlags: new Set(source.transientFlags),
  isAirborneInvincible: source.isAirborneInvincible,
  landingRecoveryFramesRemaining: source.landingRecoveryFramesRemaining,
  landingRecoveryAppliedLogicalFrame: source.landingRecoveryAppliedLogicalFrame,
});
